import { graphql } from 'gatsby';
import React, { useMemo } from 'react';

import { AccordionList } from '../components/AccordionList/AccordionList';
import Layout from '../components/Layout';
import PageTitle from '../components/PageTitle/PageTitle';
import SideBar from '../components/SideBar/SideBar';
import SEO from '../components/seo';
import { Sidebar } from '../entities/Sidebar';
import './certifications.scss';

interface ColProps {
  title: string;
  children: React.ReactNode;
}

const Col = ({ children, title }: ColProps) => {
  return (
    <div className="hwCol">
      <h4>{title}</h4>
      {children}
    </div>
  );
};

interface TableHwCertProps {
  children?: React.ReactNode;
  certification: string;
  data: any;
}

const CERTIFICATIONS = ['UKCA', 'FCC', 'CE', 'RoHS', 'RCM', 'WEEE', 'MIC', 'IC', 'GB4943', 'UL', 'KC'];
const SECTIONS = [
  { title: 'Boards', category: 'board' },
  { title: 'Shields', category: 'shield' },
  { title: 'Carriers', category: 'carrier' },
  { title: 'Kits', category: 'kit' },
];

const TableHwCert = ({ children, data, certification }: TableHwCertProps) => {
  return (
    <div className="hwTable">
      {SECTIONS.map((section, i) => {
        return (
          <Col title={section.title} key={i}>
            <CertifiedProducts data={data} certification={certification} category={section.category}></CertifiedProducts>
          </Col>
        );
      })}
      {children}
    </div>
  );
};

interface CertifiedProductsProps {
  data: any;
  certification: string;
  category: string;
}
const CertifiedProducts = ({ data, certification, category }: CertifiedProductsProps) => {
  const products = getCertifiedProducts(data, certification, category);
  return products.map((product, i) => {
    if (product.document) {
      return (
        <a key={`${i}: ${product.document}  ${product.category}`} href={product.document} target="_blank" rel="noopener noreferrer">
          {product.name}
        </a>
      );
    }
    return <>{product.name}</>;
  });
};

const CertificationItem = ({ children }: { children: React.ReactNode; title?: string }) => {
  return <>{children}</>;
};

const CertificationList = ({ children }: { children: React.ReactElement[] }) => {
  const accordions = children.map((el, i) => ({
    iconPosition: 'right' as 'right' | 'left',
    id: `${i}`,
    label: el.props.title,
    panel: <CertificationItem>{el.props.children}</CertificationItem>,
  }));

  return <AccordionList className="certifications-accordion" accordions={accordions} />;
};

/**
 * Creates a list of certified products. If a product has
 * more than one document for the requested certification, it creates
 * separate entries for those documents.
 * @param {*} data The GraphQL data to be queried
 * @param {*} certification The certification as string. e.g. "FCC"
 * @param {*} category The type of product as string. E.g. "board"
 * @returns An array of objects in the format {name : <productName>, category: <category>, document:<documentURL>}
 */
const getCertifiedProducts = (data: any, certification: string, category: string) => {
  const products = data.products.edges.filter(({ node }) => {
    return node.childMdx.frontmatter.certifications?.includes(certification) && node.fields.category === category;
  });

  let certificationList = [];

  for (let product of products) {
    const productName = product.node.childMdx.frontmatter.title;
    const certificationDocuments = data.certificationFiles.edges.filter((certFile) => {
      const relativeDir = certFile.node.relativeDirectory;
      const productDirectory = relativeDir.substring(0, relativeDir.lastIndexOf('/'));
      return productDirectory === product.node.relativeDirectory && certFile.node.name.includes(`_${certification}`);
    });

    if (certificationDocuments.length === 0) {
      certificationList.push({ name: productName, category: product.node.fields.category });
      continue;
    }

    for (let certificationDocument of certificationDocuments) {
      const filePathParts = certificationDocument.node.name.split('_');
      const hasExtraSuffix = filePathParts[filePathParts.length - 2] === certification;
      const title = hasExtraSuffix ? `${productName} (${filePathParts[filePathParts.length - 1]})` : productName;
      certificationList.push({
        name: title,
        category: product.node.fields.category,
        document: certificationDocument.node.publicURL,
      });
    }
  }
  return certificationList;
};

export default function Certifications({ data }: any) {
  function getSideBarDataRefactored(data: any) {
    let refactoredData: Sidebar[] = [];

    CERTIFICATIONS.forEach((certification) => {
      const nestedCat = SECTIONS.map((section) => {
        const products = getCertifiedProducts(data, certification, section.category);
        const nestedChild = products.map((product) => {
          return {
            title: product.name,
            url: product.document,
          };
        });
        return { category: section.title, nestedChildren: nestedChild };
      });

      refactoredData.push({
        firstLevelCategory: certification,
        image: null,
        nestedCategories: nestedCat,
      });
    });

    return refactoredData;
  }
  const certificationsRefactoredData = useMemo(() => {
    return getSideBarDataRefactored(data);
  }, [data]);

  return (
    <Layout>
      <SEO title="Certifications" description="An overview declarations of confirmity for Arduino products" />
      <main className="page-container">
        <SideBar links={[]} nestedCategories={certificationsRefactoredData} actualPage={'Certifications'} />
        <div className="page-layout certifications">
          <PageTitle
            title="Declarations of Conformity"
            description="Connected devices nearly always require certifications that ensure they operate within the wireless
            transmission specifications determined by international regulatory bodies like the FCC and CE. Below is an
            overview declarations of confirmity for Arduino products."
          />
          <CertificationList>
            <CertificationItem title="UKCA (UK Conformity Assessed)">
              <span>
                <blockquote>
                  &quot;The UKCA (UK Conformity Assessed) marking is a new UK product marking that is used for goods being placed on the market in
                  Great Britain (England, Wales and Scotland). It covers most goods which previously required the CE marking, known as ‘new approach’
                  goods.&quot;
                </blockquote>
                <strong>Source:</strong>{' '}
                <a className="bold" href="https://www.gov.uk/guidance/using-the-ukca-marking">
                  www.gov.uk
                </a>
              </span>
              <TableHwCert data={data} certification="UKCA" />
            </CertificationItem>
            <CertificationItem title="FCC">
              <span>
                <blockquote>
                  &quot;The Federal Communications Commission regulates interstate and international communications by radio, television, wire,
                  satellite, and cable in all 50 states, the District of Columbia and U.S. territories. An independent U.S. government agency overseen
                  by Congress, the Commission is the federal agency responsible for implementing and enforcing America’s communications law and
                  regulations.&quot;
                </blockquote>
                <strong>Source:</strong> <a href="https://www.fcc.gov/about/overview">www.fcc.gov</a>
              </span>
              <TableHwCert data={data} certification="FCC"></TableHwCert>
            </CertificationItem>
            <CertificationItem title="REACH">
              <span>
                <blockquote>
                  REACH is a regulation of the European Union, adopted to improve the protection of human health and the environment from the risks
                  that can be posed by chemicals, while enhancing the competitiveness of the EU chemicals industry. It also promotes alternative
                  methods for the hazard assessment of substances in order to reduce the number of tests on animals.{' '}
                </blockquote>
                <strong>Source:</strong> <a href="https://echa.europa.eu/regulations/reach/understanding-reach">echa.europa.eu</a>
              </span>
              <p>
                <li>
                  <a href="https://docs.arduino.cc/resources/reach/arduino-reach.pdf">Download REACH certification document for Arduino boards.</a>
                </li>
              </p>
            </CertificationItem>

            <CertificationItem title="CE">
              <span>
                <blockquote>
                  &quot;The letters &apos;CE&apos; appear on many products traded on the extended Single Market in the European Economic Area (EEA).
                  They signify that products sold in the EEA have been assessed to meet high safety, health, and environmental protection
                  requirements. CE marking also supports fair competition by holding all companies accountable to the same rules.&quot;
                </blockquote>
                <strong>Source:</strong> <a href="https://ec.europa.eu/growth/single-market/ce-marking_en">ec.europa.eu</a>
              </span>
              <TableHwCert data={data} certification="CE"></TableHwCert>

              <h3>RoHS</h3>
              <span>
                <blockquote>
                  &quot;Restriction of Hazardous Substances in Electrical and Electronic Equipment (RoHS). EU rules restricting the use of hazardous
                  substances in electrical and electronic equipment to protect the environment and public health.&quot;
                </blockquote>
                <strong>Source:</strong> <a href="https://ec.europa.eu/environment/topics/waste-and-recycling/rohs-directive_en">ec.europa.eu</a>
              </span>
              <TableHwCert data={data} certification="RoHS"></TableHwCert>
            </CertificationItem>
            <CertificationItem title="RCM">
              <span>
                <blockquote>
                  &quot;The Regulatory Compliance Mark RCM is a trade mark owned by the electrical regulator (Regulatory Authorities (RAs)) and
                  Australian Communications Media Authority (ACMA).&quot;
                </blockquote>
                <strong>Source:</strong> <a href="https://www.eess.gov.au/rcm/regulatory-compliance-mark-rcm-general/">eess.gov.au</a>
              </span>
              <TableHwCert data={data} certification="RCM"></TableHwCert>
            </CertificationItem>
            <CertificationItem title="WEEE">
              <span>
                <blockquote>
                  &quot;Waste from Electrical and Electronic Equipment (WEEE). EU rules on treating waste electrical and electronic equipment, to
                  contribute to sustainable production and consumption.&quot;
                </blockquote>
                <strong>Source:</strong>{' '}
                <a href="https://ec.europa.eu/environment/topics/waste-and-recycling/waste-electrical-and-electronic-equipment-weee_en">
                  ec.europa.eu
                </a>
              </span>
              <TableHwCert data={data} certification="WEEE"></TableHwCert>
            </CertificationItem>

            <CertificationItem title="MIC">
              <span>
                <blockquote>
                  &quot;Specified radio equipment such as cellular phone, wireless LAN and business purpose radio used in Japan is required to conform
                  to the technical standards regulated by the Ministry of Internal Affairs and Communications (MIC) of Japan.&quot;
                </blockquote>
                <strong>Source:</strong> <a href="https://www.telec.or.jp/eng/services/tech/">telec.or.jp</a>
              </span>
              <TableHwCert data={data} certification="MIC"></TableHwCert>
            </CertificationItem>

            <CertificationItem title="ISED/IC">
              <span>
                <blockquote>
                  &quot;Innovation, Science and Economic Development Canada (ISED) works with Canadians in all areas of the economy and in all parts
                  of the country to improve conditions for investment, enhance Canada&apos;s innovation performance, increase Canada&apos;s share of
                  global trade and build a fair, efficient and competitive marketplace&quot;
                </blockquote>
                <strong>Source:</strong> <a href="http://www.ic.gc.ca/eic/site/icgc.nsf/eng/home">www.ic.gc.ca</a>
              </span>
              <TableHwCert data={data} certification="IC"></TableHwCert>
            </CertificationItem>
            <CertificationItem title="GB4943">
              <TableHwCert data={data} certification="GB4943"></TableHwCert>
            </CertificationItem>
            <CertificationItem title="UL/cUL">
              <span>
                <blockquote>&quot;UL standards aim to prevent users of electric products from risks of fire and electric shock.&quot;</blockquote>
                <strong>Source:</strong> <a href="https://www.ul.com/">www.ul.com</a>
              </span>
              <TableHwCert data={data} certification="UL"></TableHwCert>
            </CertificationItem>
            <CertificationItem title="KC">
              <span>
                <blockquote>
                  &quot;The KC certification’s primary purpose is to identify and eliminate potential hazards to the health and safety of
                  consumers.&quot;
                </blockquote>
                <strong>Source:</strong> <a href="https://www.korea-certification.com/en/kc/what-is-kc-certification/">www.korea-certification.com</a>
              </span>
              <TableHwCert data={data} certification="KC"></TableHwCert>
            </CertificationItem>
          </CertificationList>
        </div>
      </main>
    </Layout>
  );
}

export const pageQuery = graphql`
  query {
    certifications: file(name: { eq: "certifications" }) {
      id
      childMdx {
        body
      }
    }
    products: allFile(filter: { base: { eq: "product.md" }, relativeDirectory: { regex: "/^hardware/" } }) {
      edges {
        node {
          relativeDirectory
          fields {
            category
          }
          childMdx {
            frontmatter {
              title
              certifications
            }
          }
        }
      }
    }
    certificationFiles: allFile(filter: { relativeDirectory: { regex: "/certifications$/" } }) {
      edges {
        node {
          publicURL
          name
          relativeDirectory
        }
      }
    }
  }
`;
